import { useState } from 'react'
import { usePost } from '../utils/apiComponent/useApi'
import { endpoints } from '../utils/endpoints'
import { toastUtils } from '../utils/toast'
import { useAuth } from './useAuth'
import { useCategories } from './useCategories'
import { prepareCourseData } from '../utils/courseFormData'
import { logger } from '../utils/logger'
import { api } from '../utils/apiComponent/apiClient'

export function useCourseCreation() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Utiliser l'utilisateur depuis le contexte d'authentification
  const { user } = useAuth()
  const { getDefaultCategory } = useCategories()
  
  // Hook pour créer un cours
  const { execute: createCourseApi } = usePost(endpoints.createCourse, { immediate: false })
  
  // Hook pour créer un module
  const { execute: createModuleApi } = usePost(endpoints.createModule, { immediate: false })
  
  // Hook pour créer une leçon
  const { execute: createLessonApi } = usePost(endpoints.createModuleLesson, { immediate: false })
  
  // Hook pour créer un bloc
  const { execute: createBlockApi } = usePost(endpoints.createModuleBlock, { immediate: false })
  
  // Hook pour créer un quiz
  const { execute: createQuizApi } = usePost(endpoints.createQuiz, { immediate: false })

  const createCourse = async (courseData) => {
    setLoading(true)
    setError(null)
    
    try {
      // Vérifier que l'utilisateur est connecté
      if (!user?.id) {
        throw new Error("Utilisateur non connecté")
      }

      // Récupérer une catégorie par défaut
      const defaultCategory = getDefaultCategory()
      
      // Préparer les données avec les valeurs par défaut
      const preparedData = {
        title: courseData.title,
        description: courseData.description,
        shortDescription: courseData.shortDescription || courseData.description?.substring(0, 100),
        price: courseData.price || 0,
        originalPrice: courseData.originalPrice || 0,
        currency: courseData.currency || "XOF",
        duration: courseData.duration || 0,
        level: courseData.level || "BEGINNER",
        language: courseData.language || "Français",
        status: courseData.status === "published" ? "PUBLISHED" : "DRAFT",
        isPublished: courseData.status === "published" || false,
        isFeatured: courseData.isFeatured || false,
        isFree: courseData.isFree !== undefined ? courseData.isFree : (courseData.price === 0 || !courseData.price),
        maxStudents: courseData.maxStudents || 100,
        requirements: courseData.requirements || [],
        objectives: courseData.objectives || [],
        certificate: courseData.certificate !== undefined ? courseData.certificate : false,
        accessDurationDays: courseData.accessDurationDays || 90,
        metaTitle: courseData.metaTitle || courseData.title,
        metaDescription: courseData.metaDescription || courseData.description,
        keywords: courseData.keywords || [],
        creatorId: user.id,
        categoryId: courseData.categoryId || defaultCategory?.id,
        tagIds: courseData.tagIds || [],
        // Fichiers (si présents, seront détectés par prepareCourseData)
        thumbnailFile: courseData.thumbnailFile,
        // URLs (si pas de fichiers)
        thumbnail: courseData.thumbnail
      }

      // Préparer les données (FormData si fichiers, sinon JSON)
      const requestData = prepareCourseData(preparedData)
      
      logger.debug('Creating course', {
        hasFiles: !!preparedData.thumbnailFile,
        isFormData: requestData instanceof FormData
      })

      // Utiliser api.post directement pour gérer FormData correctement
      const response = await api.post(endpoints.createCourse, requestData)
      
      // L'API retourne directement l'objet cours, pas encapsulé dans { data: ... }
      if (response && typeof response === 'object' && response.id) {
        logger.info('Course created successfully', { courseId: response.id })
        toastUtils.saveSuccess()
        return response
      } else if (response?.data) {
        logger.info('Course created successfully', { courseId: response.data.id })
        toastUtils.saveSuccess()
        return response.data
      } else {
        throw new Error("Erreur lors de la création du cours")
      }
    } catch (err) {
      logger.error('Error creating course', err, {
        title: courseData.title,
        userId: user?.id
      })
      const errorMessage = err?.message || err?.details?.message || "Erreur lors de la création du cours"
      setError(errorMessage)
      toastUtils.serverError()
      throw err
    } finally {
      setLoading(false)
    }
  }

  const createModule = async (moduleData, courseId) => {
    setLoading(true)
    setError(null)
    
    try {
      const apiData = {
        title: moduleData.title,
        description: moduleData.description,
        order: moduleData.order || 1,
        duration: parseInt(moduleData.duration) || 0,
        isPublished: true,
        courseId: courseId,
        creatorId: user?.id || "temp-creator" // Utiliser l'ID de l'utilisateur connecté
      }

      const response = await createModuleApi(apiData)
      
      // L'API retourne directement l'objet module, pas encapsulé dans { data: ... }
      if (response && typeof response === 'object' && response.id) {
        toastUtils.saveSuccess()
        return response
      } else if (response?.data) {
        toastUtils.saveSuccess()
        return response.data
      } else {
        throw new Error("Erreur lors de la création du module")
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la création du module"
      setError(errorMessage)
      toastUtils.serverError()
      throw err
    } finally {
      setLoading(false)
    }
  }

  const createLesson = async (lessonData, moduleId) => {
    setLoading(true)
    setError(null)
    
    try {
      const apiData = {
        title: lessonData.title,
        description: lessonData.description || '',
        order: lessonData.order || 1,
        duration: parseInt(lessonData.duration) || 0,
        isPublished: lessonData.isPublished !== undefined ? lessonData.isPublished : false,
        moduleId: moduleId,
        creatorId: user?.id || "temp-creator"
      }

      const response = await createLessonApi(apiData)
      
      // L'API retourne directement l'objet leçon, pas encapsulé dans { data: ... }
      if (response && typeof response === 'object' && response.id) {
        toastUtils.saveSuccess()
        return response
      } else if (response?.data) {
        toastUtils.saveSuccess()
        return response.data
      } else {
        throw new Error("Erreur lors de la création du chapitre")
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la création du chapitre"
      setError(errorMessage)
      toastUtils.serverError()
      throw err
    } finally {
      setLoading(false)
    }
  }

  const createBlock = async (blockData, lessonId) => {
    setLoading(true)
    setError(null)
    
    try {
      const blockType = blockData.type?.toUpperCase() || blockData.type
      // L'API attend 'image' et 'video' (pas 'imageFile' et 'videoFile')
      const hasImageFile = (blockData.image instanceof File) || (blockData.imageFile instanceof File)
      const hasVideoFile = (blockData.video instanceof File) || (blockData.videoFile instanceof File)
      
      // Si des fichiers sont présents, utiliser FormData
      if (hasImageFile || hasVideoFile) {
        const formData = new FormData()
        
        // Champs de base
        formData.append('title', blockData.title)
        if (blockData.description) {
          formData.append('description', blockData.description)
        }
        formData.append('type', blockType)
        formData.append('order', (blockData.order || 1).toString())
        if (blockData.duration) {
          formData.append('duration', parseInt(blockData.duration).toString())
        }
        formData.append('isPublished', 'true')
        formData.append('lessonId', lessonId)
        formData.append('creatorId', user?.id || "temp-creator")
        
        // Ajouter les fichiers (l'API attend 'image' et 'video')
        const imageFile = blockData.image || blockData.imageFile
        const videoFile = blockData.video || blockData.videoFile
        if (imageFile instanceof File) {
          formData.append('image', imageFile)
        }
        if (videoFile instanceof File) {
          formData.append('video', videoFile)
        }
        
        // Ajouter le contenu selon le type de bloc
        // Pour IMAGE avec upload : ne pas envoyer content.alt et content.caption
        // Pour VIDEO avec upload : envoyer seulement content.transcript
        if (blockData.content) {
          if (typeof blockData.content === 'object') {
            // Pour IMAGE avec upload, ne pas envoyer content (le backend ne le veut pas)
            if (blockType === 'IMAGE' && imageFile instanceof File) {
              // Ne rien envoyer dans content pour IMAGE avec upload
              // alt et caption ne sont pas nécessaires selon l'erreur
            } else if (blockType === 'VIDEO' && videoFile instanceof File) {
              // Pour VIDEO avec upload, envoyer seulement transcript
              if (blockData.content.transcript) {
                formData.append('content.transcript', blockData.content.transcript)
              }
            } else {
              // Pour les autres cas (sans upload), envoyer tout le content
              Object.keys(blockData.content).forEach(key => {
                if (blockData.content[key] !== undefined && blockData.content[key] !== null && blockData.content[key] !== '') {
                  if (typeof blockData.content[key] === 'object') {
                    // Pour les objets imbriqués, les convertir en JSON string
                    formData.append(`content.${key}`, JSON.stringify(blockData.content[key]))
                  } else {
                    // Pour les valeurs primitives (string, number, etc.), les convertir en string
                    const value = blockData.content[key].toString()
                    formData.append(`content.${key}`, value)
                  }
                }
              })
            }
          } else {
            // Si content est une chaîne primitive, l'envoyer directement
            formData.append('content', blockData.content.toString())
          }
        }
        
        // Utiliser api.post directement pour gérer FormData
        const response = await api.post(endpoints.createModuleBlock, formData)
        
        if (response && typeof response === 'object' && response.id) {
          toastUtils.saveSuccess()
          return response
        } else if (response?.data) {
          toastUtils.saveSuccess()
          return response.data
        } else {
          throw new Error("Erreur lors de la création du bloc")
        }
      } else {
        // Sinon, utiliser JSON comme avant
      let content = {}
      
        switch (blockType) {
          case "TEXT":
            // Pour TEXT, blockData.content est un objet avec html, wordCount, readingTime
            // S'assurer que html est présent et non vide
            if (blockData.content && typeof blockData.content === 'object') {
              content = {
                html: blockData.content.html || '<p>Contenu du bloc</p>',
                wordCount: blockData.content.wordCount || 0,
                readingTime: blockData.content.readingTime || 0
              }
            } else {
              // Fallback si content n'est pas un objet
              content = {
                html: '<p>Contenu du bloc</p>',
                wordCount: 0,
                readingTime: 0
              }
            }
          break
          case "IMAGE":
            content = blockData.content || { imageUrl: blockData.imageUrl || '' }
          break
          case "VIDEO":
            content = blockData.content || { videoUrl: blockData.videoUrl || '' }
          break
        default:
            content = blockData.content || {}
      }

      const apiData = {
        title: blockData.title,
        description: blockData.description || "",
          type: blockType,
        content: content,
        order: blockData.order || 1,
        duration: parseInt(blockData.duration) || 0,
        isPublished: true,
        lessonId: lessonId,
          creatorId: user?.id || "temp-creator"
      }

      const response = await createBlockApi(apiData)
      
      if (response && typeof response === 'object' && response.id) {
        toastUtils.saveSuccess()
        return response
      } else if (response?.data) {
        toastUtils.saveSuccess()
        return response.data
      } else {
        throw new Error("Erreur lors de la création du bloc")
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la création du bloc"
      setError(errorMessage)
      toastUtils.serverError()
      throw err
    } finally {
      setLoading(false)
    }
  }

  const createQuiz = async (quizData, moduleId) => {
    setLoading(true)
    setError(null)
    
    try {
      // Valider que moduleId est fourni et convertir en chaîne si nécessaire
      if (!moduleId) {
        throw new Error("Un module doit être sélectionné pour créer un quiz")
      }
      
      // Convertir moduleId en chaîne si c'est un nombre
      const moduleIdString = String(moduleId)
      
      // Vérifier si un quiz existe déjà pour ce module
      try {
        const existingQuizResponse = await api.get(`${endpoints.quiz}?moduleId=${moduleIdString}`)
        if (existingQuizResponse?.quizzes && existingQuizResponse.quizzes.length > 0) {
          const existingQuiz = existingQuizResponse.quizzes[0]
          throw new Error(`Ce module a déjà un quiz. Veuillez modifier le quiz existant (ID: ${existingQuiz.id}) au lieu d'en créer un nouveau.`)
        }
      } catch (checkError) {
        // Si l'erreur est "Ce module a déjà un quiz", la propager
        if (checkError?.message?.includes('déjà un quiz')) {
          throw checkError
        }
        // Sinon, ignorer l'erreur (peut être 404 si aucun quiz n'existe)
      }
      
      const apiData = {
        title: quizData.title,
        description: quizData.description || "",
        instructions: quizData.instructions || "Répondez à toutes les questions.",
        timeLimit: quizData.timeLimit ? parseInt(quizData.timeLimit) : null, // null = pas de limite
        passingScore: parseInt(quizData.passingScore) || 100,
        maxAttempts: parseInt(quizData.maxAttempts) || 3,
        isRandomized: quizData.isRandomized || false,
        isPublished: quizData.isPublished !== undefined ? quizData.isPublished : true,
        moduleId: moduleIdString, // S'assurer que c'est une chaîne
        creatorId: user?.id || "temp-creator"
      }

      console.log('createQuiz appelé avec apiData:', apiData)
      // Utiliser api.post directement pour gérer la réponse correctement
      const response = await api.post(endpoints.createQuiz, apiData)
      console.log('api.post a retourné:', response)
      
      // L'API retourne directement l'objet quiz, pas encapsulé dans { data: ... }
      // Mais apiClient extrait data de { success: true, data: {...} }
      if (response && typeof response === 'object' && response.id) {
        console.log('Quiz créé avec id:', response.id)
        toastUtils.saveSuccess()
        return response
      } else if (response?.data) {
        console.log('Quiz créé avec id (dans data):', response.data.id)
        toastUtils.saveSuccess()
        return response.data
      } else {
        console.error('Réponse invalide de api.post:', response)
        throw new Error("Erreur lors de la création du quiz")
      }
    } catch (err) {
      logger.error('Error creating quiz', err)
      // Si l'erreur est 422 "Ce module a déjà un quiz", afficher un message plus clair
      if (err?.status === 422 || err?.message?.includes('déjà un quiz')) {
        const errorMessage = err?.message || err?.details?.errors?.[0] || err?.response?.data?.errors?.[0] || "Ce module a déjà un quiz. Veuillez modifier le quiz existant au lieu d'en créer un nouveau."
        setError({ ...err, message: errorMessage })
        toastUtils.serverError()
        throw { ...err, message: errorMessage }
      }
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la création du quiz"
      setError(errorMessage)
      toastUtils.serverError()
      throw err
    } finally {
      setLoading(false)
    }
  }

  const createCompleteCourse = async (courseData) => {
    setLoading(true)
    setError(null)
    
    try {
      // 1. Créer le cours
      const course = await createCourse(courseData)
      
      // 2. Créer les modules (leçons)
      const modules = []
      for (let i = 0; i < courseData.lessons.length; i++) {
        const lesson = courseData.lessons[i]
        const module = await createModule({
          ...lesson,
          order: i + 1
        }, course.id)
        modules.push(module)
        
        // 3. Créer les blocs pour chaque module
        if (lesson.blocks && lesson.blocks.length > 0) {
          for (let j = 0; j < lesson.blocks.length; j++) {
            const block = lesson.blocks[j]
            
            // Si c'est un quiz, créer un quiz séparé
            if (block.type === "quiz") {
              await createQuiz({
                ...block,
                order: j + 1
              }, module.id)
            } else {
              // Sinon, créer un bloc normal
              await createBlock({
                ...block,
                order: j + 1
              }, module.id)
            }
          }
        }
      }
      
      toastUtils.saveSuccess()
      return { course, modules }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la création du cours complet"
      setError(errorMessage)
      toastUtils.serverError()
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    error,
    createCourse,
    createModule,
    createLesson,
    createBlock,
    createQuiz,
    createCompleteCourse
  }
}
